import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Empty, Spin, Typography, Pagination, Card, Tag, Space, Statistic, Tooltip, Alert, Button, Select, Avatar } from 'antd';
import { 
  BankOutlined, 
  FileTextOutlined, 
  DollarOutlined, 
  CalendarOutlined,
  ClockCircleOutlined,
  Loading3QuartersOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  PaperClipOutlined
} from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import BatchLoadingSkeleton from '../Batches/BatchLoadingSkeleton';
import SearchInput from '../SearchInput';
import PACommentsDisplay from '../PACommentsDisplay';
import StatusStripe, { normalizeAggregatedItemsStatus } from '../StatusStripe';
import { getClaimsArchiveScope, withClaimsArchiveScope, canManageClaimsArchive, buildClaimsArchiveRequest } from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal as getClaimPayable,
  getDisplayCost as getClaimDisplayCost,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import ArchiveClaimsModal from '../ArchiveClaimsModal';
import { uri_img } from '../../../assets/utils/http-request';
import { useAuth } from '../../../assets/utils/useAuth';
import {
  DATE_AMOUNT_ITEM_SORT_OPTIONS,
  TAT_SORT_OPTIONS,
  parseSortSelectValue,
  sortSelectValue,
  SORT_BY,
  SORT_DIR,
} from '../../../utils/tableSortParams';
import './AdminFacilityClaimsList.css';
import { tatFromRecord } from '../../../utils/turnaroundTime';

const { Text, Title } = Typography;

// Status configuration matching ClaimsAdminNewHook
const STATUS_CONFIG = {
  0: { 
    text: 'Pending CO', 
    color: '#121221', 
    icon: <ClockCircleOutlined />,
    bgColor: '#f0f0f0',
    description: 'Claims pending CO review'
  },
  1: { 
    text: 'Pending HM', 
    color: '#ff9200', 
    icon: <Loading3QuartersOutlined />,
    bgColor: '#fff7e6',
    description: 'Claims pending HM approval'
  },
  2: { 
    text: 'Pending CFO', 
    color: '#afaa0b', 
    icon: <ExclamationCircleOutlined />,
    bgColor: '#fffbe6',
    description: 'Claims pending CFO approval'
  },
  3: { 
    text: 'Paid', 
    color: '#00aa00', 
    icon: <CheckCircleOutlined />,
    bgColor: '#f6ffed',
    description: 'Claims that have been paid'
  },
  4: { 
    text: 'Denied', 
    color: '#f5222d', 
    icon: <CloseCircleOutlined />,
    bgColor: '#fff1f0',
    description: 'Claims that have been denied'
  },
};

/**
 * AdminFacilityClaimsList - Phase 2: Displays claims for selected facility, month, and status (Admin)
 * Loads claims on-demand when status card is expanded
 * @param {string|object} month - Month in YYYY-MM format or object with month property
 * @param {number|object} facilityId - Facility ID or facility object
 * @param {number|null} statusCode - Optional status code filter (0, 1, 2, 3, 4). When provided, only loads claims for that status.
 * @param {function} onClaimClick - Callback when a claim is clicked
 */
const AdminFacilityClaimsList = ({ month, facilityId, statusCode = null, onClaimClick }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const CLAIMS_SEARCH_PARAM = 'adminClaimsSearch';
  const CLAIMS_PAGE_PARAM = 'adminClaimsPage';
  const CLAIMS_PAGE_SIZE_PARAM = 'adminClaimsPageSize';
  const [claims, setClaims] = useState([]); // Claims from API (backend handles filtering)
  const [facility, setFacility] = useState(null);
  const [aggregates, setAggregates] = useState(null); // Aggregates from API response
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [listSort, setListSort] = useState({ sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC });
  const fetchingRef = useRef(false);
  const lastRequestRef = useRef({ month: null, facilityId: null, page: null });
  const archiveScope = getClaimsArchiveScope(searchParams);
  const canArchiveClaims = canManageClaimsArchive(user);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveModalMode, setArchiveModalMode] = useState('archive');
  const [archiveRequest, setArchiveRequest] = useState(null);

  // Get search query from URL
  const searchQuery = searchParams.get(CLAIMS_SEARCH_PARAM) || '';

  const getClaimKey = (claim) => claim.claims_id || claim.checkin_id;

  const getMonthKey = () => {
    if (!month) return null;
    if (typeof month === 'string') return month;
    return month.month || month.key || null;
  };

  const getFacilityIdNum = () => (
    typeof facilityId === 'object' ? (facilityId.facility_id || facilityId.id) : facilityId
  );

  const openGroupArchive = (mode = 'archive') => {
    setArchiveRequest(buildClaimsArchiveRequest({
      scope: 'facility',
      month: getMonthKey(),
      facilityId: getFacilityIdNum(),
      // This list is a single status queue, and the archive has to match it.
      // `statusCode` is 0 for Pending CO, so it goes through
      // buildClaimsArchiveRequest's null-check, never a truthiness test.
      segment: statusCode,
    }));
    setArchiveModalMode(mode);
    setArchiveModalOpen(true);
  };

  // Reset refs and pagination when month, facilityId, or statusCode changes
  useEffect(() => {
    if (month && facilityId) {
      const monthKey = typeof month === 'string' ? month : (month.month || month.key || month);
      const facilityIdNum = typeof facilityId === 'object' ? (facilityId.facility_id || facilityId.id) : facilityId;
      
      // If month, facility, or statusCode changed, reset refs, pagination, and aggregates
      if (lastRequestRef.current.month !== monthKey || 
          lastRequestRef.current.facilityId !== facilityIdNum ||
          lastRequestRef.current.statusCode !== statusCode) {
        lastRequestRef.current = { month: null, facilityId: null, statusCode: null, page: null };
        fetchingRef.current = false;
        setPagination((prev) => ({ ...prev, current: 1 }));
        setAggregates(null); // Reset aggregates when parameters change
        // Keep URL/page state in sync with new drill-down context.
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(CLAIMS_SEARCH_PARAM);
        newParams.set(CLAIMS_PAGE_PARAM, '1');
        newParams.delete(CLAIMS_PAGE_SIZE_PARAM);
        window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
      }
    }
  }, [month, facilityId, statusCode, searchParams]);

  const fetchClaims = useCallback(async (page = 1, search = '') => {
    if (!month || !facilityId) return;

    // Get stable keys
    const monthKey = typeof month === 'string' ? month : (month.month || month.key || month);
    const facilityIdNum = typeof facilityId === 'object' ? (facilityId.facility_id || facilityId.id) : facilityId;
    
    // Prevent duplicate calls only if currently fetching the same request
    if (fetchingRef.current && 
        lastRequestRef.current.month === monthKey && 
        lastRequestRef.current.facilityId === facilityIdNum && 
        lastRequestRef.current.statusCode === statusCode &&
        lastRequestRef.current.page === page &&
        lastRequestRef.current.search === search) {
      return;
    }
    
    fetchingRef.current = true;
    lastRequestRef.current = { month: monthKey, facilityId: facilityIdNum, statusCode, page, search };

    setLoading(true);
    try {
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        statusCode: statusCode,
        sort_by: listSort.sort_by,
        sort_dir: listSort.sort_dir,
        ...(search && search.trim() ? { search: search.trim() } : {}),
      }, archiveScope);
      const response = await lib.getAdminFacilityClaims(monthKey, facilityIdNum, params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch claims',
        });
        return;
      }

      // Backend returns: { error, message, data: { month, month_label, facility: {...}, claims: [...] }, aggregates: {...}, pagination: {...}, search: {...} }
      const responseData = response?.data || {};
      const data = responseData.data || {};
      const claimsList = Array.isArray(data.claims) ? data.claims : [];
      
      // Backend handles all filtering (status + search)
      setClaims(claimsList);
      setFacility(data.facility || null);
      setAggregates(responseData.aggregates || null); // Extract aggregates from API response
      setSearchMeta(responseData.search || null); // Store search metadata
      
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || claimsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching claims:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch claims',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [month, facilityId, statusCode, pagination.pageSize, archiveScope, listSort]);

  // Track if this is the initial load (to avoid using parent search)
  const initialLoadRef = useRef(true);
  const hasSearchedRef = useRef(false); // Track if user has actively searched at this level
  
  // Fetch claims when month, facilityId, statusCode, search, or page changes
  useEffect(() => {
    if (month && facilityId && statusCode !== null) {
      const page = parseInt(searchParams.get(CLAIMS_PAGE_PARAM) || pagination.current.toString());
      const urlSearch = searchParams.get(CLAIMS_SEARCH_PARAM) || '';
      
      // On initial load (when navigating from facilities), don't use parent search
      // But if user has searched at this level, use the search from URL
      // We detect this by checking if user has actively searched (hasSearchedRef) OR
      // if search exists in URL after initial load
      const shouldUseSearch = hasSearchedRef.current || (!initialLoadRef.current && urlSearch.trim());
      
      fetchClaims(page, shouldUseSearch ? urlSearch : '');
      
      // Mark that initial load is complete after first fetch
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
      }
    } else {
      // Clear claims if statusCode is not provided
      setClaims([]);
      // Reset flags when statusCode changes
      initialLoadRef.current = true;
      hasSearchedRef.current = false;
    }
  }, [month, facilityId, statusCode, pagination, fetchClaims, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    // Update URL with new page
    const newParams = new URLSearchParams(searchParams);
    newParams.set(CLAIMS_PAGE_PARAM, page.toString());
    if (pageSize) {
      newParams.set(CLAIMS_PAGE_SIZE_PARAM, pageSize.toString());
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
  };

  const handleSearch = (query) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    // Mark that user has actively searched at this level
    hasSearchedRef.current = true;
    initialLoadRef.current = false;
    // Trigger immediate fetch so user sees results without waiting for effect
    fetchClaims(1, query);
  };

  const formatMonthLabel = (monthString) => {
    const date = dayjs(monthString + '-01');
    return date.format('MMMM YYYY');
  };

  const getSubmittedToPACost = useCallback((claim) => getSubmittedTotal(claim), []);

  const getPAApprovedCost = useCallback((claim) => getPaApprovedTotal(claim), []);

  const getClaimsApprovedCost = useCallback((claim) => getClaimPayable(claim), []);

  const getDisplayCost = useCallback((claim) => getClaimDisplayCost(claim), []);

  const getSourceMeta = useCallback((claim) => {
    const mode = String(
      claim?.source_mode ||
      (claim?.source !== undefined ? (Number(claim.source) === 1 ? 'manual' : 'direct') : 'direct')
    ).toLowerCase();
    const manualCount = Number(claim?.manual_items_count || 0);
    const directCount = Number(claim?.direct_items_count || 0);
    return {
      color: mode === 'mixed' ? 'purple' : (mode === 'manual' ? 'orange' : 'green'),
      label: mode === 'mixed'
        ? `Mixed${manualCount || directCount ? ` (M:${manualCount} D:${directCount})` : ''}`
        : (mode === 'manual' ? 'Manual' : 'Direct'),
    };
  }, []);

  // Use aggregates from API response, fallback to calculated statistics if aggregates not available
  const statistics = useMemo(() => {
    // Prefer aggregates from API (more accurate, includes all claims not just current page)
    if (aggregates) {
      return {
        totalClaims: aggregates.claims_count || aggregates.total_claims || 0,
        submittedTotal: getSubmittedTotal(aggregates),
        paApprovedTotal: getPaApprovedTotal(aggregates),
        claimsApprovedTotal: getClaimPayable(aggregates),
        uniquePatients: aggregates.unique_enrollees || 0,
        averageClaimCost: aggregates.average_claim_cost || 0,
        claimsWithQueries: aggregates.claims_with_queries || 0,
        totalItemsCount: aggregates.total_items_count || 0
      };
    }
    
    // Fallback: Calculate from current claims (filtered by statusCode and filters)
    const submittedTotal = claims.reduce((sum, claim) => sum + getSubmittedToPACost(claim), 0);
    const paApprovedTotal = claims.reduce((sum, claim) => sum + getPAApprovedCost(claim), 0);
    const claimsApprovedTotal = claims.reduce((sum, claim) => sum + getClaimsApprovedCost(claim), 0);

    return {
      totalClaims: claims.length,
      submittedTotal,
      paApprovedTotal,
      claimsApprovedTotal,
      uniquePatients: new Set(claims.map(c => c.insurance_no)).size,
      averageClaimCost: claims.length > 0 ? claimsApprovedTotal / claims.length : 0,
      claimsWithQueries: claims.filter(c => Number(c.queries) > 0).length,
      totalItemsCount: claims.reduce((sum, claim) => sum + (Number(claim.pa_items_count) || 0), 0)
    };
  }, [aggregates, claims, getSubmittedToPACost, getPAApprovedCost, getClaimsApprovedCost]);

  const formatStatus = (flag) => {
    if (!flag) return { text: 'Unknown', color: '#999' };
    if (typeof flag === 'object') {
      return { text: flag.text || 'Unknown', color: flag.color || '#999' };
    }
    return { text: 'Unknown', color: '#999' };
  };

  if (!month || !facilityId || statusCode === null) {
    return <Empty description="No month, facility, or status selected" />;
  }

  // When statusCode is provided (Phase 2), we only show filtered claims
  const hasClaims = claims.length > 0;
  const statusLabel = STATUS_CONFIG[statusCode]?.text || `Status ${statusCode}`;

  return (
    <div className="admin-facility-claims-list">
      {/* Header */}
      <div className="admin-facility-claims-list__header">
        <div>
          <BankOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <Title level={2} style={{ margin: 0, display: 'inline' }}>
            {facility?.facility_name || 'Facility'}
          </Title>
          <Text type="secondary" style={{ marginLeft: '12px' }}>
            {typeof month === 'string' ? formatMonthLabel(month) : formatMonthLabel(month.month || month.key)}
          </Text>
        </div>
        {facility?.facility_address && (
          <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
            {facility.facility_address}, {facility.facility_city}, {facility.facility_state}
          </Text>
        )}
      </div>

      {/* Statistics - Only show when statusCode is provided (Phase 2) */}
      {statusCode !== null && (
        <div className="admin-facility-claims-list__stats" style={{ marginBottom: '0px' }}>
          <Row gutter={[16, 16]}>
            {/* Total Claims Card */}
            <Col xs={24} sm={12} md={6} lg={6}>
              <Tooltip title={`Total number of ${STATUS_CONFIG[statusCode]?.text || 'claims'} for this facility`}>
                <Card 
                  className="stat-card stat-card--claims" 
                  style={{ 
                    borderRadius: '16px', 
                    cursor: 'help',
                    background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
                    border: '2px solid #1890ff',
                    boxShadow: '0 4px 16px rgba(24, 144, 255, 0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px', height: '100%' }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(24, 144, 255, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', gap: '12px', position: 'relative', zIndex: 1 }}>
                    {/* Left Content */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                      {/* Title */}
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', lineHeight: 1.3, marginBottom: '8px' }}>
                        Total Claims
                      </Text>
                      
                      {/* Value */}
                      <Text style={{ fontSize: '26px', fontWeight: 600, color: '#0050b3', lineHeight: 1.2, marginBottom: '6px' }}>
                        {statistics.totalClaims}
                      </Text>
                      
                      {/* Divider and Description */}
                      <div>
                        <div style={{ height: '1px', background: 'rgba(24, 144, 255, 0.3)', marginBottom: '4px' }} />
                        <Text style={{ fontSize: '11px', color: '#434343', lineHeight: 1.3 }}>
                          {STATUS_CONFIG[statusCode]?.text || `Status ${statusCode}`}
                        </Text>
                      </div>
                    </div>
                    
                    {/* Right Icon - Centered */}
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(24, 144, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#1890ff',
                      flexShrink: 0
                    }}>
                      <FileTextOutlined />
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            {/* Submitted to PA */}
            <Col xs={24} sm={12} md={6} lg={6}>
              <Tooltip title="Total amount the facility submitted for pre-authorization">
                <Card 
                  className="stat-card stat-card--cost" 
                  style={{ 
                    borderRadius: '16px', 
                    cursor: 'help',
                    background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                    border: '2px solid #2f54eb',
                    boxShadow: '0 4px 16px rgba(47, 84, 235, 0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px', height: '100%' }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(47, 84, 235, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', lineHeight: 1.3, marginBottom: '8px' }}>
                        Submitted to PA
                      </Text>
                      <Text style={{ fontSize: '24px', fontWeight: 600, color: '#1d39c4', lineHeight: 1.2, marginBottom: '6px' }}>
                        <CurrencyFormat
                          value={statistics.submittedTotal || 0}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={2}
                        />
                      </Text>
                      <div>
                        <div style={{ height: '1px', background: 'rgba(47, 84, 235, 0.3)', marginBottom: '4px' }} />
                        <Text style={{ fontSize: '11px', color: '#434343', lineHeight: 1.3 }}>
                          Facility billed amount
                        </Text>
                      </div>
                    </div>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(47, 84, 235, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#2f54eb',
                      flexShrink: 0
                    }}>
                      <DollarOutlined />
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            {/* PA approved */}
            <Col xs={24} sm={12} md={6} lg={6}>
              <Tooltip title="Total amount approved on pre-authorization">
                <Card 
                  className="stat-card stat-card--approved" 
                  style={{ 
                    borderRadius: '16px', 
                    cursor: 'help',
                    background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                    border: '2px solid #722ed1',
                    boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px', height: '100%' }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', lineHeight: 1.3, marginBottom: '8px' }}>
                        PA approved
                      </Text>
                      <Text style={{ fontSize: '24px', fontWeight: 600, color: '#531dab', lineHeight: 1.2, marginBottom: '6px' }}>
                        <CurrencyFormat
                          value={statistics.paApprovedTotal || 0}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={2}
                        />
                      </Text>
                      <div>
                        <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginBottom: '4px' }} />
                        <Text style={{ fontSize: '11px', color: '#434343', lineHeight: 1.3 }}>
                          Authorized by PA
                        </Text>
                      </div>
                    </div>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(114, 46, 209, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#722ed1',
                      flexShrink: 0
                    }}>
                      <DollarOutlined />
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            {/* Claims approved (payable) */}
            <Col xs={24} sm={12} md={6} lg={6}>
              <Tooltip title="Effective payable after claims adjudication (0 if claims-rejected)">
                <Card 
                  className="stat-card stat-card--cost" 
                  style={{ 
                    borderRadius: '16px', 
                    cursor: 'help',
                    background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                    border: '2px solid #52c41a',
                    boxShadow: '0 4px 16px rgba(82, 196, 26, 0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px', height: '100%' }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(82, 196, 26, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', lineHeight: 1.3, marginBottom: '8px' }}>
                        Claims approved
                      </Text>
                      <Text style={{ fontSize: '24px', fontWeight: 600, color: '#389e0d', lineHeight: 1.2, marginBottom: '6px' }}>
                        <CurrencyFormat
                          value={statistics.claimsApprovedTotal || 0}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'₦'}
                          decimalScale={2}
                        />
                      </Text>
                      <div>
                        <div style={{ height: '1px', background: 'rgba(82, 196, 26, 0.3)', marginBottom: '4px' }} />
                        <Text style={{ fontSize: '11px', color: '#434343', lineHeight: 1.3 }}>
                          Payable / amount due
                        </Text>
                      </div>
                    </div>
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(82, 196, 26, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#52c41a',
                      flexShrink: 0
                    }}>
                      <CheckCircleOutlined />
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>

            {/* Unique Patients Card */}
            <Col xs={24} sm={12} md={6} lg={6}>
              <Tooltip title="Number of unique patients (by insurance number) in this status">
                <Card 
                  className="stat-card stat-card--patients" 
                  style={{ 
                    borderRadius: '16px', 
                    cursor: 'help',
                    background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                    border: '2px solid #722ed1',
                    boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '20px', height: '100%' }}
                >
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', gap: '12px', position: 'relative', zIndex: 1 }}>
                    {/* Left Content */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                      {/* Title */}
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', lineHeight: 1.3, marginBottom: '8px' }}>
                        Unique Patients
                      </Text>
                      
                      {/* Value */}
                      <Text style={{ fontSize: '26px', fontWeight: 600, color: '#531dab', lineHeight: 1.2, marginBottom: '6px' }}>
                        {statistics.uniquePatients}
                      </Text>
                      
                      {/* Divider and Description */}
                      <div>
                        <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginBottom: '4px' }} />
                        <Text style={{ fontSize: '11px', color: '#434343', lineHeight: 1.3 }}>
                          Distinct insurance numbers
                        </Text>
                      </div>
                    </div>
                    
                    {/* Right Icon - Centered */}
                    <div style={{ 
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(114, 46, 209, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: '#722ed1',
                      flexShrink: 0
                    }}>
                      <UserOutlined />
                    </div>
                  </div>
                </Card>
              </Tooltip>
            </Col>
          </Row>
        </div>
      )}

      {/* Visual Separator and Header with Search */}
      {statusCode !== null && (
        <div style={{ 
          marginBottom: '24px', 
          paddingTop: '24px',
          borderTop: '2px solid #e8e8e8',
          position: 'relative'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <Title level={4} style={{ margin: 0, color: '#262626' }}>
              Claims List
            </Title>
            <Space>
              <Select
                value={sortSelectValue(listSort.sort_by, listSort.sort_dir)}
                options={[...DATE_AMOUNT_ITEM_SORT_OPTIONS, ...TAT_SORT_OPTIONS]}
                onChange={(value) => {
                  setListSort(parseSortSelectValue(value));
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                style={{ minWidth: 170 }}
              />
              <SearchInput 
                placeholder="Search claims (patient name, insurance, claim ID, complaint)..." 
                onSearch={handleSearch}
                loading={loading}
                searchParamName={CLAIMS_SEARCH_PARAM}
                pageParamName={CLAIMS_PAGE_PARAM}
              />
              {canArchiveClaims ? (
                <Button
                  onClick={() => openGroupArchive(archiveScope === 'archived' ? 'unarchive' : 'archive')}
                >
                  {archiveScope === 'archived'
                    ? `Unarchive ${statusLabel} this month`
                    : `Archive ${statusLabel} this month`}
                </Button>
              ) : null}
            </Space>
          </div>

          {/* Search Results Info */}
          {searchMeta && searchMeta.applied && (
            <Alert
              message={
                <Space>
                  <span>Found <strong>{searchMeta.results_count || 0}</strong> result{searchMeta.results_count !== 1 ? 's' : ''} for</span>
                  <Tag color="blue">"{searchMeta.query}"</Tag>
                </Space>
              }
              type="info"
              showIcon
              closable
              style={{ marginBottom: '16px' }}
              onClose={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete(CLAIMS_SEARCH_PARAM);
                newParams.set(CLAIMS_PAGE_PARAM, '1');
                window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
                handleSearch('');
              }}
            />
          )}
        </div>
      )}

      {/* Search Input for when statusCode is null (shouldn't happen in normal flow, but keeping for safety) */}
      {statusCode === null && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <Title level={4} style={{ margin: 0, color: '#262626' }}>
              Claims
            </Title>
            <SearchInput 
              placeholder="Search claims (patient name, insurance, claim ID, complaint)..." 
              onSearch={handleSearch}
              loading={loading}
              searchParamName={CLAIMS_SEARCH_PARAM}
              pageParamName={CLAIMS_PAGE_PARAM}
            />
          </div>

          {/* Search Results Info */}
          {searchMeta && searchMeta.applied && (
            <Alert
              message={
                <Space>
                  <span>Found <strong>{searchMeta.results_count || 0}</strong> result{searchMeta.results_count !== 1 ? 's' : ''} for</span>
                  <Tag color="blue">"{searchMeta.query}"</Tag>
                </Space>
              }
              type="info"
              showIcon
              closable
              style={{ marginBottom: '16px' }}
              onClose={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete(CLAIMS_SEARCH_PARAM);
                newParams.set(CLAIMS_PAGE_PARAM, '1');
                window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
                handleSearch('');
              }}
            />
          )}
        </div>
      )}

      {/* Claims List - When statusCode is provided (Phase 2), show filtered claims */}
      <div className="admin-facility-claims-list__claims">
        {!hasClaims && !loading ? (
          <Empty
            description={searchQuery ? `No claims found for "${searchQuery}"` : `No ${statusLabel} claims found for ${facility?.facility_name || 'this facility'} in ${typeof month === 'string' ? formatMonthLabel(month) : formatMonthLabel(month.month || month.key)}`}
            style={{ padding: '40px' }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {claims.map((claim) => {
            const status = formatStatus(claim.flag);
            const claimKey = getClaimKey(claim);
            const lineCounts = normalizeAggregatedItemsStatus(
              claim.aggregatedClaimsItemsStatus || claim.aggregatedItemsStatus
            );
            return (
              <Col xs={24} sm={12} md={8} lg={6} key={claimKey}>
                <Card
                  className="claim-card"
                  hoverable
                  onClick={() => {
                    // Navigate to claims filing page with encounter query parameter
                    const encounterId = claim.checkin_id || claim.claims_id;
                    const facilityIdNum = typeof facilityId === 'object' ? (facilityId.facility_id || facilityId.id) : facilityId;
                    // Use the facility object if available, otherwise use the facility from state (which has full details)
                    const facilityObj = facility || (typeof facilityId === 'object' ? facilityId : null);
                    
                    if (encounterId && facilityIdNum !== undefined && facilityIdNum !== null) {
                      navigate(`/claims/filings/?encounter=${encodeURIComponent(encounterId)}`, {
                        state: { 
                          facility: facilityObj || facilityIdNum,  // Pass facility object if available, otherwise ID
                          segment: statusCode,   // Use the statusCode prop
                          claim: claim,
                          month: month
                        }
                      });
                    } else if (onClaimClick) {
                      // Fallback to parent handler if navigation fails
                      onClaimClick(claim);
                    } else {
                      notification.error({
                        message: 'Error',
                        description: 'Missing facility or encounter information'
                      });
                    }
                  }}
                  style={{
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <div className="claim-card__content">
                    <div className="claim-card__header">
                      <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: '14px' }}>
                          {claim.checkin_id || claim.claims_id || 'N/A'}
                        </Text>
                        <Tag color={status.color}>
                          {status.text}
                        </Tag>
                        {claim.has_pa_header_attachments ||
                        (Array.isArray(claim.pa_header_attachments) &&
                          claim.pa_header_attachments.length > 0) ? (
                          <Tag icon={<PaperClipOutlined />} color="default">
                            Docs
                          </Tag>
                        ) : null}
                        {/* Filing Source Indicator */}
                        {(() => {
                          const sourceMeta = getSourceMeta(claim);
                          return (
                            <Tag
                              color={sourceMeta.color}
                              size="small"
                              style={{
                                fontSize: '10px',
                                fontWeight: 600
                              }}
                            >
                              {sourceMeta.label}
                            </Tag>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <div className="claim-card__body">
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {lineCounts && (
                          <StatusStripe counts={lineCounts} className="claim-card__stripe" />
                        )}
                        <div className="claim-card__patient">
                          <Avatar
                            size={32}
                            src={claim.pic_name ? `${uri_img}${claim.pic_name}` : undefined}
                            icon={<UserOutlined />}
                          />
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Patient:</Text>
                            <br />
                            <Text>{claim.patient_name || 'N/A'}</Text>
                          </div>
                        </div>
                        {claim.insurance_no && (
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Insurance:</Text>
                            <br />
                            <Text>{claim.insurance_no}</Text>
                          </div>
                        )}
                        {claim.encounter_date && (
                          <div>
                            <CalendarOutlined style={{ marginRight: '4px' }} />
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {dayjs(claim.encounter_date).format('MMM D, YYYY')}
                            </Text>
                          </div>
                        )}
                        {(() => {
                          const tat = tatFromRecord(claim);
                          if (!tat.label) return null;
                          return (
                            <div>
                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                              <Text
                                type="secondary"
                                style={{ fontSize: '11px', color: tat.pending ? '#b45309' : undefined }}
                                title={tat.pending ? 'Elapsed since filed' : 'Filed to processed'}
                              >
                                TAT {tat.label}
                              </Text>
                            </div>
                          );
                        })()}
                        {(claim.display_cost !== undefined || claim.claims_approved_total !== undefined || claim.approved_total !== undefined || claim.encounter_cost !== undefined) && (
                          <div>
                            <DollarOutlined style={{ marginRight: '4px' }} />
                            <CurrencyFormat
                              value={getDisplayCost(claim)}
                              displayType={'text'}
                              thousandSeparator={true}
                              prefix={'₦'}
                              decimalScale={2}
                            />
                          </div>
                        )}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr',
                          gap: '5px',
                          marginTop: '4px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          background: '#f7faff',
                          border: '1px solid #e5eefb'
                        }}>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#475569' }}>Submitted to PA:</Text>{' '}
                            <Text style={{ fontSize: '14px', color: '#0f172a', fontWeight: 700 }}>
                              <CurrencyFormat
                                value={getSubmittedToPACost(claim)}
                                displayType={'text'}
                                thousandSeparator
                                prefix={'₦'}
                                decimalScale={2}
                              />
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#475569' }}>PA approved:</Text>{' '}
                            <Text style={{ fontSize: '14px', color: '#0f172a', fontWeight: 700 }}>
                              <CurrencyFormat
                                value={getPAApprovedCost(claim)}
                                displayType={'text'}
                                thousandSeparator
                                prefix={'₦'}
                                decimalScale={2}
                              />
                            </Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#475569' }}>Claims approved:</Text>{' '}
                            <Text style={{ fontSize: '14px', color: '#0f172a', fontWeight: 700 }}>
                              <CurrencyFormat
                                value={getClaimsApprovedCost(claim)}
                                displayType={'text'}
                                thousandSeparator
                                prefix={'₦'}
                                decimalScale={2}
                              />
                            </Text>
                          </div>
                        </div>
                        
                        {/* Batch Comment Display */}
                        {claim.batch_comment && (
                          <div style={{
                            marginTop: '8px',
                            padding: '8px 12px',
                            background: '#f5f5f5',
                            borderLeft: '3px solid #1890ff',
                            borderRadius: '4px',
                            fontSize: '11px',
                            lineHeight: '1.5',
                            color: '#666'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px',
                              marginBottom: '4px',
                              fontWeight: 600,
                              color: '#333'
                            }}>
                              <FileTextOutlined style={{ fontSize: '11px' }} />
                              <span>PA Comment</span>
                            </div>
                            <div style={{ 
                              color: '#595959',
                              fontStyle: 'italic'
                            }}>
                              {claim.batch_comment}
                            </div>
                          </div>
                        )}
                      </Space>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
          </Row>
        )}
      </div>

      {pagination.total > pagination.pageSize && (
        <div className="admin-facility-claims-list__pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} claims`
            }
          />
        </div>
      )}

      <ArchiveClaimsModal
        open={archiveModalOpen}
        mode={archiveModalMode}
        request={archiveRequest}
        segment={statusCode}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
        }}
        onSuccess={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
          fetchClaims(pagination.current, searchMeta?.query || '');
        }}
      />
    </div>
  );
};

export default AdminFacilityClaimsList;

